import React, { useState, useEffect } from "react";

export default function CoordinatorPerformanceChart({
  baseUrl = "https://cubixweberp.com:213",
  companyCode = "SUNRISEDXB",
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState("Sales"); // Alternates between 'Sales' and 'Orders'

  useEffect(() => {
    const today = new Date();
    const todayDate = today.toISOString().split("T")[0];

    // Safe 30 days calculation without object mutation
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const fromDate = thirtyDaysAgo.toISOString().split("T")[0];
    const toDate = todayDate;

    const fetchCoordinatorData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${baseUrl}/api/HRDashboard/${companyCode}/MAIN/CRM_COORDINATOR_PERFORM/${fromDate}/${toDate}/-/-/-/-/-`;
        const response = await fetch(url);
        if (!response.ok) throw new Error("Network response was not ok");

        const json = await response.json();
        const cleanData = (Array.isArray(json) ? json : []).map((item) => ({
          ...item,
          displayName: item.CoordinatorName?.trim() || item.CoordinatorID || "Unknown",
        }));

        setData(cleanData);
      } catch (err) {
        console.error("Error fetching performance chart data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (baseUrl && companyCode) {
      fetchCoordinatorData();
    }
  }, [baseUrl, companyCode]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2 text-xs bg-white rounded-xl border border-slate-100 shadow-sm">
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <span>Loading performance metrics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-xs text-rose-500 bg-white rounded-xl border border-slate-100 shadow-sm font-medium">
        Failed to load coordinator graph: {error}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="p-6 text-center text-xs text-gray-400 bg-white rounded-xl border border-slate-100 shadow-sm">
        No coordinator performance data found for this period.
      </div>
    );
  }

  // Find dynamic max values to figure out graphic height proportions
  const maxSales = Math.max(...data.map((d) => d.TotalSales || 0), 1);
  const maxOrders = Math.max(...data.map((d) => d.TotalOrders || 0), 1);
  const maxValue = metric === "Sales" ? maxSales : maxOrders;

  // Spacing parameters for graph scaling calculation
  const columnWidth = 100;
  const graphCanvasHeight = 180; // Fixed graph height canvas in pixels
  const computedChartWidth = data.length * columnWidth;

  // Create four reference marks for the Y-Axis Labels (100%, 66%, 33%, 0%)
  const yAxisTicks = [
    maxValue,
    maxValue * 0.66,
    maxValue * 0.33,
    0
  ];

  const formatYLabel = (val) => {
    if (metric === "Sales") {
      if (val >= 1000) return `${Math.round(val / 1000)}k`;
      return Math.round(val);
    }
    return Math.round(val);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 w-full mt-3">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-sm font-bold text-slate-800 tracking-tight">
            Coordinator Performance Breakdown
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Displaying data for {data.length} active coordinators
          </p>
        </div>

        {/* Toggle Controls */}
        <div className="flex items-center self-start sm:self-auto bg-slate-100 p-1 rounded-xl border border-slate-200/50 text-xs">
          <button
            type="button"
            onClick={() => setMetric("Sales")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition-all outline-none cursor-pointer ${
              metric === "Sales"
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Total Sales
          </button>
          <button
            type="button"
            onClick={() => setMetric("Orders")}
            className={`px-3 py-1.5 rounded-lg font-semibold transition-all outline-none cursor-pointer ${
              metric === "Orders"
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-slate-500 hover:text-slate-900"
            }`}
          >
            Total Orders
          </button>
        </div>
      </div>

      {/* Grid Layout separating Fixed Y-Axis from Scrollable Bars Grid */}
      <div className="flex items-start border border-slate-100 rounded-xl bg-slate-50/30 overflow-hidden">
        
        {/* FIXED Y-AXIS COLUMN SIDEBAR CONTAINER */}
        <div 
          style={{ height: `${graphCanvasHeight}px` }}
          className="w-12 flex flex-col justify-between items-end pr-2 pt-8 pointer-events-none select-none shrink-0 relative z-20 bg-white border-r border-slate-100"
        >
          {yAxisTicks.map((tick, tIdx) => (
            <span key={tIdx} className="text-[9px] font-mono text-slate-400 font-bold leading-none">
              {formatYLabel(tick)}
            </span>
          ))}
        </div>

        {/* SCROLLABLE HORIZONTAL CANVAS DECK FOR BARS & X-AXIS LABELS */}
        <div className="flex-1 overflow-x-auto pb-4 scrollbar-thin touch-pan-x">
          <div
            style={{ width: `${computedChartWidth}px` }}
            className="flex flex-col pt-8 relative px-4"
          >
            {/* Background Grid Guidelines matching our Y-axis intervals */}
            <div
              style={{ height: `${graphCanvasHeight}px` }}
              className="absolute left-0 right-0 top-8 flex flex-col justify-between pointer-events-none border-b border-slate-200/60"
            >
              <div className="w-full border-t border-dashed border-slate-200/60" />
              <div className="w-full border-t border-dashed border-slate-200/60" />
              <div className="w-full border-t border-dashed border-slate-200/60" />
            </div>

            {/* Render Deck for Physical Graph Bars */}
            <div
              style={{ height: `${graphCanvasHeight}px` }}
              className="flex items-end justify-between relative z-10 w-full"
            >
              {data.map((item, idx) => {
                const currentVal = metric === "Sales" ? (item.TotalSales || 0) : (item.TotalOrders || 0);
                
                // Map the numerical data directly to exact layout pixels relative to graphCanvasHeight
                const calculatedPixelHeight = Math.max((currentVal / maxValue) * graphCanvasHeight, 10);

                return (
                  <div
                    key={item.CoordinatorID + idx}
                    style={{ width: `${columnWidth}px` }}
                    className="flex flex-col items-center justify-end h-full group shrink-0"
                  >
                    {/* Text value label pinned atop the bar */}
                    <span className="text-[10px] font-mono text-slate-600 font-bold mb-1.5 opacity-90">
                      {metric === "Sales"
                        ? `${Math.round(currentVal / 1000)}k`
                        : currentVal}
                    </span>

                    {/* Concrete solid visible Bar */}
                    <div
                      style={{ height: `${calculatedPixelHeight}px` }}
                      className={`w-12 rounded-t-md transition-all duration-300 group-hover:brightness-95 ${
                        metric === "Sales"
                          ? "bg-indigo-500 border border-indigo-600 shadow-sm shadow-indigo-100"
                          : "bg-emerald-500 border border-emerald-600 shadow-sm shadow-emerald-100"
                      }`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Flat Horizontal Labels Row (No Slant) */}
            <div className="border-t border-slate-200 flex items-start justify-between z-10 pt-3 mt-1.5 bg-white/40">
              {data.map((item, idx) => (
                <div
                  key={idx}
                  style={{ width: `${columnWidth}px` }}
                  className="text-center font-bold text-[10px] text-slate-600 px-1 truncate select-none shrink-0"
                  title={item.displayName}
                >
                  {item.displayName}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Swipe prompt footer */}
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400 sm:hidden">
        <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
        <span>Swipe horizontally to view all {data.length} coordinators.</span>
      </div>
    </div>
  );
}